/**
 * WebSocket client for SAT Agents with JWT authentication.
 */

import { getWebSocketUrl } from '../config/api';

class WebSocketClient {
  constructor() {
    this.ws = null;
    this.sessionId = null;
    this.token = null;
    this.tokenExpiresAt = null;
    this.agentName = null;
    this.messageHandlers = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.refreshTimer = null;
    this.lastEventSequence = 0;
  }

  /**
   * Connect to WebSocket server with JWT token.
   * @param {string} token - JWT token from localStorage
   * @param {string} agentName - Optional agent name
   * @param {string} existingSessionId - Optional session ID to resume
   */
  connect(token, agentName = 'sat-coordinator', existingSessionId = null) {
    if (!token) {
      console.error('Token is required for WebSocket connection');
      return Promise.reject(new Error('Token is required'));
    }

    this.token = token;
    this.agentName = agentName;

    return new Promise((resolve, reject) => {
      try {
        // Connect to WebSocket using centralized configuration
        const wsUrl = getWebSocketUrl();
        console.log('[WebSocketClient] Connecting to:', wsUrl);
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          this.reconnectAttempts = 0;

          // Send INIT message with token
          this.send({
            type: 'INIT',
            token: this.token,
            agentName: agentName,
            sessionId: existingSessionId
          });
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message, resolve, reject);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          reject(error);
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket closed:', event.code, event.reason);
          this.clearRefreshTimer();

          // Attempt reconnection if not a policy violation
          if (event.code !== 1008 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.attemptReconnect(agentName, existingSessionId);
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Handle incoming WebSocket messages.
   */
  handleMessage(message, resolve, reject) {
    console.log('WebSocket message:', message);

    switch (message.type) {
      case 'SESSION_CREATED':
        this.sessionId = message.sessionId;
        this.lastEventSequence = 0; // New session, reset sequence
        console.log('Session created:', this.sessionId);
        if (resolve) resolve(this.sessionId);
        break;

      case 'RECONNECTED':
        this.sessionId = message.sessionId;
        console.log(`Reconnected, ${message.missedEvents || 0} events to replay`);
        if (resolve) resolve(this.sessionId);
        break;

      case 'TOKEN_REFRESHED':
        this.tokenExpiresAt = message.expiresAt;
        console.log('Token refreshed, expires at:', new Date(this.tokenExpiresAt));
        this.scheduleTokenRefresh();
        break;

      case 'ERROR':
        console.error('WebSocket error:', message.content);
        if (reject) reject(new Error(message.content));

        // Notify handlers about error
        this.notifyHandlers(message);
        break;

      case 'PARTIAL':
      case 'THINKING':
      case 'TOOL_CALL':
      case 'FINISH':
      case 'REQUIRE_PERMISSION':
        // Track event sequence for reconnection replay
        this.lastEventSequence++;
        // Forward to message handlers
        this.notifyHandlers(message);
        break;

      default:
        console.warn('Unknown message type:', message.type);
    }
  }

  /**
   * Send a message to the agent.
   * @param {string} text - Message text
   */
  sendMessage(text) {
    if (!this.sessionId) {
      console.error('No active session');
      return;
    }

    this.send({
      type: 'MESSAGE',
      text: text
    });
  }

  /**
   * Send permission response for HITL.
   * @param {string} commandId - Command ID
   * @param {boolean} granted - Whether permission is granted
   */
  sendPermissionResponse(commandId, granted) {
    this.send({
      type: 'PERMISSION_RESPONSE',
      commandId: commandId,
      granted: granted
    });
  }

  /**
   * Refresh the JWT token.
   * @param {string} newToken - New JWT token
   */
  refreshToken(newToken) {
    if (!newToken) {
      console.error('New token is required');
      return;
    }

    this.token = newToken;
    this.send({
      type: 'REFRESH_TOKEN',
      token: newToken
    });
  }

  /**
   * Schedule automatic token refresh 5 minutes before expiry.
   */
  scheduleTokenRefresh() {
    this.clearRefreshTimer();

    if (!this.tokenExpiresAt) {
      return;
    }

    const now = Date.now();
    const expiresAt = this.tokenExpiresAt;
    const refreshAt = expiresAt - (5 * 60 * 1000); // 5 minutes before expiry
    const delay = refreshAt - now;

    if (delay > 0) {
      this.refreshTimer = setTimeout(() => {
        console.log('Token refresh needed');
        // Emit event for app to handle token refresh
        this.notifyHandlers({
          type: 'TOKEN_REFRESH_NEEDED'
        });
      }, delay);
    }
  }

  /**
   * Clear token refresh timer.
   */
  clearRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Attempt to reconnect to WebSocket.
   * Uses RECONNECT message type to trigger event replay on the backend.
   */
  attemptReconnect(agentName, existingSessionId) {
    this.reconnectAttempts++;
    const reconnectSessionId = this.sessionId || existingSessionId;
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

    if (!reconnectSessionId) {
      // No session to reconnect to — fall back to fresh INIT
      console.warn('No sessionId for reconnect, falling back to INIT');
      setTimeout(() => {
        this.connect(this.token, agentName, existingSessionId)
          .catch(error => {
            console.error('Reconnection failed:', error);
          });
      }, this.reconnectDelay);
      return;
    }

    setTimeout(() => {
      this.reconnect(reconnectSessionId)
        .catch(error => {
          console.error('Reconnection failed:', error);
        });
    }, this.reconnectDelay);
  }

  /**
   * Reconnect to an existing session using the RECONNECT message type.
   * This avoids full re-initialization and replays only missed events.
   * @param {string} sessionId - Session ID to reconnect to
   */
  reconnect(sessionId) {
    return new Promise((resolve, reject) => {
      try {
        const wsUrl = getWebSocketUrl();
        console.log('[WebSocketClient] Reconnecting to:', wsUrl, 'session:', sessionId);
        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('[WebSocketClient] WebSocket open, sending RECONNECT');
          this.reconnectAttempts = 0;
          const reconnectMsg = {
            type: 'RECONNECT',
            token: this.token,
            sessionId: sessionId,
            lastEventSequence: this.lastEventSequence
          };
          if (this.agentName) {
            reconnectMsg.agentName = this.agentName;
          }
          this.send(reconnectMsg);
        };

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            this.handleMessage(message, resolve, reject);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket reconnect error:', error);
          reject(error);
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket closed after reconnect:', event.code, event.reason);
          this.clearRefreshTimer();
          if (event.code !== 1008 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.attemptReconnect(this.agentName, sessionId);
          }
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Register a message handler.
   * @param {Function} handler - Handler function
   */
  onMessage(handler) {
    this.messageHandlers.push(handler);
  }

  /**
   * Unregister a message handler.
   * @param {Function} handler - Handler function
   */
  offMessage(handler) {
    this.messageHandlers = this.messageHandlers.filter(h => h !== handler);
  }

  /**
   * Notify all message handlers.
   */
  notifyHandlers(message) {
    this.messageHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('Error in message handler:', error);
      }
    });
  }

  /**
   * Send a raw message to WebSocket.
   */
  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not connected');
    }
  }

  /**
   * Close the WebSocket connection.
   */
  close() {
    this.clearRefreshTimer();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.sessionId = null;
    this.lastEventSequence = 0;
  }

  /**
   * Check if WebSocket is connected.
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

export default WebSocketClient;
